import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryApi, FetchArgs } from '@reduxjs/toolkit/query/react';
import { TestFilterBody, TestFilterResult } from '../interfaces/test.interface';
import { clearUser, setUser } from './reducers/user.slice';
import { setError } from './reducers/ui.slice';
import { isFetchBaseQueryError } from '../utils/isFetchBaseQueryError';
import { Role, AuthContextGroup } from '../interfaces/auth.interface';
import getErrorStatus from '../utils/getErrorStatus';

const BASE_URL = `${import.meta.env.VITE_BACKEND_URL}`;

export interface MyProfileData {
  nameAndSurname: string;
  nickName: string;
  email: string;
  company: string;
  jobTitle: string;
  department: string;
  location: string;
  testFinishedEmails?: boolean;
  image: string;
  groups?: activeGroup[];
  role?: Role;
  id?: string | null;
  activeGroup?: activeGroup;
}

interface activeGroup {
  id: string;
  name: string;
  description?: string;
  runTask?: boolean;
  adminExtractData?: boolean;
  modifyEmbargoKeyword?: boolean;
  modifyUrlExceptions?: boolean;
  modifySkuChecker?: boolean;
}

interface SearchIdRequest {
  url: string;
  body: { id: string };
}

export interface SearchIdResponse {
  label: string;
  value: string;
}

export interface ServerError {
  status: number;
  data: {
    message: string;
  };
}

export interface ChangeImageArgs {
  id: string;
  image: string;
}

export interface LoginResponse {
  userId: string;
  token: string;
  role: Role;
  expirationDate: Date | null;
  group: AuthContextGroup;
  adminGroup: string[];
  email: string;
}

export type AuthSessionResponse = LoginResponse;

export type TwoFactorMethod = 'totp' | 'email';

export interface TwoFactorChallengeRequiredResponse {
  twoFactorRequired: true;
  method: 'choice';
  challengeToken: string;
  hasTotp: boolean;
}

export type LoginMutationResponse = AuthSessionResponse | TwoFactorChallengeRequiredResponse;

export const isTwoFactorChallengeResponse = (
  res: LoginMutationResponse
): res is TwoFactorChallengeRequiredResponse =>
  (res as TwoFactorChallengeRequiredResponse).twoFactorRequired === true;

export interface TwoFactorSetupResponse {
  otpauthUrl: string;
  qrCodeDataUrl: string;
  secret: string;
}

interface BaseQueryWithReAuthOptions {
  baseUrl: string;
}

export const baseQueryWithReAuth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: object,
  options?: BaseQueryWithReAuthOptions
) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: options?.baseUrl,
    credentials: 'include'
  });
  const result = await baseQuery(args, api, extraOptions);

  switch (result.error?.status) {
    case 401:
      api.dispatch(clearUser());
      return result;
    case 404:
      try {
        api.dispatch(
          setError(isFetchBaseQueryError(result.error) ? result.error.data?.message : 'Resource not found')
        );
      } catch {
        // Prevent Immer reducer errors from breaking the query chain
      }
      return result;
    default:
      return result;
  }
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: (...args) => baseQueryWithReAuth(...args, { baseUrl: BASE_URL }),
  tagTypes: ['SingleReports', 'MyProfile', 'SearchId', 'Relogin'],
  endpoints: (build) => ({
    getSingleReports: build.query<TestFilterResult, TestFilterBody>({
      query: (body) => ({
        url: 'api/athena/results/flag/testfilter',
        method: 'POST',
        body
      }),
      providesTags: ['SingleReports']
    }),
    getMyProfile: build.query<MyProfileData, { id: string | null }>({
      query: ({ id }) => ({
        url: `api/users/myprofile/${id}`,
        method: 'GET'
      }),
      providesTags: ['MyProfile']
    }),
    putMyProfile: build.mutation<MyProfileData, { body: MyProfileData }>({
      query: ({ body }) => ({
        url: `api/users/changeData`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['MyProfile']
    }),
    searchTestBulkId: build.mutation<SearchIdResponse[], SearchIdRequest>({
      query: ({ url, body }) => ({
        url: `${url}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['SearchId']
    }),
    login: build.mutation<LoginMutationResponse, { email: string; password: string; rememberMe: boolean }>({
      query: ({ email, password, rememberMe }) => ({
        url: 'api/users/login',
        method: 'POST',
        body: { email, password, rememberMe }
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (!isTwoFactorChallengeResponse(data)) {
            dispatch(setUser(data));
          }
        } catch (error) {
          console.log('[api.login]', error);
        }
      }
    }),
    setupTwoFactor: build.mutation<TwoFactorSetupResponse, { setupToken: string }>({
      query: ({ setupToken }) => ({
        url: 'api/users/2fa/setup',
        method: 'POST',
        headers: { Authorization: `Bearer ${setupToken}` }
      })
    }),
    sendTwoFactorEmailCode: build.mutation<void, { challengeToken: string }>({
      query: ({ challengeToken }) => ({
        url: 'api/users/2fa/challenge/send-email-code',
        method: 'POST',
        body: { challengeToken }
      })
    }),
    sendTwoFactorSetupEmailCode: build.mutation<void, { challengeToken: string }>({
      query: ({ challengeToken }) => ({
        url: 'api/users/2fa/setup/send-email-code',
        method: 'POST',
        body: { challengeToken }
      })
    }),
    verifyTwoFactorSetupEmailCode: build.mutation<{ setupToken: string }, { challengeToken: string; code: string }>({
      query: ({ challengeToken, code }) => ({
        url: 'api/users/2fa/setup/verify-email-code',
        method: 'POST',
        body: { challengeToken, code }
      })
    }),
    enableTwoFactor: build.mutation<AuthSessionResponse, { setupToken: string; code: string }>({
      query: ({ setupToken, code }) => ({
        url: 'api/users/2fa/enable',
        method: 'POST',
        headers: { Authorization: `Bearer ${setupToken}` },
        body: { code }
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser(data));
        } catch (error) {
          console.log('[api.enableTwoFactor]', error);
        }
      }
    }),
    verifyTwoFactorChallenge: build.mutation<
      AuthSessionResponse,
      { challengeToken: string; code: string; method: TwoFactorMethod }
    >({
      query: ({ challengeToken, code, method }) => ({
        url: 'api/users/2fa/challenge',
        method: 'POST',
        body: { challengeToken, code, method }
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser(data));
        } catch (error) {
          console.log('[api.verifyTwoFactorChallenge]', error);
        }
      }
    }),
    disableTwoFactor: build.mutation<void, { password: string; code: string }>({
      query: ({ password, code }) => ({
        url: 'api/users/2fa/disable',
        method: 'POST',
        body: { password, code }
      })
    }),
    changeImage: build.mutation<MyProfileData, ChangeImageArgs>({
      query: ({ id, image }) => ({
        url: `api/users/changeimg`,
        method: 'POST',
        body: { id, image }
      }),
      invalidatesTags: ['MyProfile']
    }),
    relogin: build.query<LoginResponse, void>({
      query: () => ({
        url: 'api/users/relogin',
        method: 'GET'
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser(data));
        } catch (error) {
          const status = getErrorStatus(error);
          console.log('status', status);
          if (status === 403) {
            dispatch(clearUser());
            window.location.href = '/login';
          }
          console.log('[api.login]', error);
        }
      },
      providesTags: ['Relogin']
    }),
    logout: build.mutation<void, void>({
      query: () => ({
        url: 'api/users/logout',
        method: 'GET'
      }),
      onQueryStarted: async (_, { dispatch }) => {
        dispatch(clearUser());
        window.location.href = '/login';
      }
    })
  })
});

export const {
  useGetSingleReportsQuery,
  useGetMyProfileQuery,
  useLazyGetMyProfileQuery,
  usePutMyProfileMutation,
  useSearchTestBulkIdMutation,
  useLoginMutation,
  useChangeImageMutation,
  useSetupTwoFactorMutation,
  useEnableTwoFactorMutation,
  useVerifyTwoFactorChallengeMutation,
  useDisableTwoFactorMutation,
  useSendTwoFactorEmailCodeMutation,
  useSendTwoFactorSetupEmailCodeMutation,
  useVerifyTwoFactorSetupEmailCodeMutation
} = api;
